import * as Apollo from '@apollo/client';
import { ApolloError } from '@apollo/client';
import {
    AudienceCreateInputType,
    AudienceDataFragmentDoc,
    AudienceDataGraphql,
    AudienceGraphql,
    AudienceUpdateInputType,
    GetAudiencesListsDocument,
    GetAudiencesListsQuery,
    GetAudiencesListsQueryVariables,
    GetRecipientsDocument,
    useCreateAudienceMutation as _useCreateAudienceMutation,
    useDeleteAudienceMutation as _useDeleteAudienceMutation,
    useGetAudiencesListsQuery,
    useUpdateAudiencesMutation
} from '../../../../generated/apollo-hooks';
import { shallowEqual, useDispatch, useStore } from 'react-redux';
import { enqueueSnackbar } from '../../../state/actions/ui';
import { useCallback } from 'react';
import _map from 'lodash/map';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import { AudiencePublishStatus, ErrorTypes } from '@walkme/audiences-types';
import { confirmDialog } from '../../Dialogs/ConfirmDialog/ConfirmDialog';
import { confirmDelete } from '../../Dialogs/DeleteDialog/DeleteDialog';
import { AudiencesTabs } from '../../../state/reducers/audiences';
import { addAudiencesProcessingRowIds, removeAudiencesProcessingRowIds, triggerRowAnimationAction } from '../../../state/actions/audiences';
import { hasRulesChanged } from '../CreateAudienceDialog/utils';
import { SNACKBAR_ACTION_TYPES } from '../../AppSnackbarProvider/AppSnackbarProvider';
import useSelector from '../../../infrastructure/useSelector';
import logger from '../../../services/logger.service';
import IStore from '../../../infrastructure/store.interface';
import { Store } from 'redux';
import { AUDIENCE_UPDATE_ERRORS } from '@communication-center/data-objects';
import { AppTableRowAnimateType } from '../../AppTable/model';
import { getOperationName } from '@apollo/client/utilities';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const keyPrefix = 'components.audiences.hooks';

export const useAudiencesLists = (baseOptions?: Apollo.QueryHookOptions<GetAudiencesListsQuery, GetAudiencesListsQueryVariables>) => {
    const { systemId, authorized, supportedSystemType } = useSelector(
        state => ({
            systemId: state.common.currentSystem,
            authorized: state.common.authorized,
            supportedSystemType: state.common.supportedSystemType
        }),
        shallowEqual
    );

    const skip = !(authorized && supportedSystemType && systemId);

    const { data, loading, error } = useGetAudiencesListsQuery({
        ...baseOptions,
        variables: { ...baseOptions?.variables, systemId },
        skip
    });
    const audiencesList = data?.getAudiencesLists?.audiencesList;
    const archivedAudiencesList = data?.getAudiencesLists?.archivedAudiencesList;
    return { audiencesList, archivedAudiencesList, loading, error };
};

enum OpNames {
    create,
    update
}
type Operations = {
    [OpNames.create]: AudienceCreateInputType;
    [OpNames.update]: AudienceUpdateInputType;
};

const parseAudience = <T extends OpNames>(audience: NotAllRecipientsAudience | AudienceUpdateInputType | AudienceCreateInputType, opName: T) => {
    const fields = ['title', 'description', 'data', 'user'] as const;
    let parsed;
    if (opName === OpNames.create) {
        parsed = _pick({ ...(audience as AudienceCreateInputType) }, ...fields);
    } else {
        parsed = _pick({ ...(audience as AudienceUpdateInputType) }, 'id', 'publishState', 'size', 'sizeTotal', 'sizeInactive', ...fields);
    }
    if (parsed.data == null) {
        delete parsed.data;
    }
    if (parsed?.user) {
        parsed.user = _omit(audience.user, 'role');
    }
    return parsed as Operations[T];
};

const archive = async ({
    mutateFunction,
    audiences,
    clientCache,
    systemId,
    store,
    t
}: {
    mutateFunction: ReturnType<typeof useUpdateAudiencesMutation>[0];
    audiences: AudienceGraphql[];
    clientCache: $TSFixMe;
    systemId: string;
    store: Store<IStore>;
    t: TFunction;
}) => {
    const currentTab = store.getState().audiences.currentTab;
    const mapped = _map(audiences as NotAllRecipientsAudience[], audience =>
        parseAudience({ ...audience, publishState: AudiencePublishStatus.Archived }, OpNames.update)
    );
    try {
        const { data } = await mutateFunction({
            variables: {
                systemId,
                updateRules: false,
                audiences: mapped
            }
        });

        await store.dispatch(
            triggerRowAnimationAction({
                ids: data!.updateAudiences.updatedIds,
                animateType: currentTab === AudiencesTabs.Active ? AppTableRowAnimateType.deleted : AppTableRowAnimateType.created,
                result: data!.updateAudiences,
                onDoneBulk: async results => {
                    const audiences = results.map(result => result.audiences).flat();
                    const updatedIds = results.map(result => result.updatedIds).flat();

                    await clientCache.updateQuery(
                        { query: GetAudiencesListsDocument, variables: { systemId } },
                        ({ getAudiencesLists }: $TSFixMe) => ({
                            getAudiencesLists: {
                                ...getAudiencesLists,
                                audiencesList: getAudiencesLists.audiencesList.filter(({ id }: $TSFixMe) => !updatedIds.includes(id)),
                                archivedAudiencesList: [...getAudiencesLists.archivedAudiencesList, ...audiences]
                            }
                        })
                    );
                }
            }) as $TSFixMe
        );

        store.dispatch(
            enqueueSnackbar({
                message: t('archive.archived', { count: audiences.length, title: audiences[0].title }),
                action: {
                    callback: () => {
                        activate({ mutateFunction, audiences, clientCache, systemId, store, t });
                    },
                    type: SNACKBAR_ACTION_TYPES.undo
                },
                options: { variant: 'success' }
            })
        );
    } catch (error) {
        const { isBusinessLogicError, msg } = getErrorMessageHandlerProps({error: error as ApolloError, t});
        if (!isBusinessLogicError) {
            logger.error('archiveAudience error', error);
        }
        store.dispatch(
            enqueueSnackbar({
                message: isBusinessLogicError ? msg : t('archive.error', { count: audiences.length }),
                options: { variant: 'error' }
            })
        );
    }
};

type NotAllRecipientsAudience = AudienceGraphql & { data: AudienceDataGraphql };

const activate = async ({
    mutateFunction,
    audiences,
    clientCache,
    systemId,
    store,
    t
}: {
    mutateFunction: ReturnType<typeof useUpdateAudiencesMutation>[0];
    audiences: AudienceGraphql[];
    clientCache: $TSFixMe;
    systemId: string;
    store: Store<IStore>;
    t: TFunction;
}) => {
    const currentTab = store.getState().audiences.currentTab;

    try {
        const { data } = await mutateFunction({
            variables: {
                systemId,
                audiences: _map(audiences as NotAllRecipientsAudience[], audience =>
                    parseAudience({ ...audience, publishState: AudiencePublishStatus.Published }, OpNames.update)
                ),
                updateRules: false
            }
        });

        await store.dispatch(
            triggerRowAnimationAction({
                ids: data!.updateAudiences.updatedIds,
                animateType: currentTab === AudiencesTabs.Archived ? AppTableRowAnimateType.modified : AppTableRowAnimateType.created,
                result: data!.updateAudiences,
                onDoneBulk: async results => {
                    const audiences = results.map(result => result.audiences).flat();
                    const updatedIds = results.map(result => result.updatedIds).flat();

                    await clientCache.updateQuery(
                        { query: GetAudiencesListsDocument, variables: { systemId } },
                        ({ getAudiencesLists }: $TSFixMe) => ({
                            getAudiencesLists: {
                                ...getAudiencesLists,
                                audiencesList: [...getAudiencesLists.audiencesList, ...audiences],
                                archivedAudiencesList: getAudiencesLists.archivedAudiencesList.filter(({ id }: $TSFixMe) => !updatedIds.includes(id))
                            }
                        })
                    );
                }
            }) as $TSFixMe
        );

        store.dispatch(
            enqueueSnackbar({
                message: t('activate.activated', { count: audiences.length, title: audiences[0].title }),
                action: {
                    callback: () => {
                        archive({ mutateFunction, audiences, clientCache, systemId, store, t });
                    },
                    type: SNACKBAR_ACTION_TYPES.undo
                },
                options: { variant: 'success' }
            })
        );
    } catch (error) {
        store.dispatch(
            enqueueSnackbar({
                message: t('activate.error', { count: audiences.length }),
                options: { variant: 'error' }
            })
        );
        logger.error('activate audience error', error);
    }
};

export const useActivateAudiencesMutation = () => {
    const { t } = useTranslation('', { keyPrefix });
    const dispatch = useDispatch();
    const store = useStore();
    const [mutateFunction, { client }] = useUpdateAudiencesMutation({ fetchPolicy: 'no-cache' });
    const systemId = useSelector(store => store.common.currentSystem);

    return useCallback(
        async (audiences: AudienceGraphql[]) => {
            dispatch(addAudiencesProcessingRowIds(AudiencesTabs.Archived, audiences));
            await activate({ mutateFunction, audiences, clientCache: client.cache, systemId, store, t });
            dispatch(removeAudiencesProcessingRowIds(AudiencesTabs.Archived, audiences));
        },
        [dispatch, mutateFunction, client.cache, systemId, store, t]
    );
};

export const useArchiveAudiencesMutation = () => {
    const { t } = useTranslation('', { keyPrefix: `${keyPrefix}.useArchiveAudiencesMutation` });
    const { t: t_without_prefix } = useTranslation('', { keyPrefix });
    const dispatch = useDispatch();
    const store = useStore();
    const systemId = useSelector(store => store.common.currentSystem);
    const [mutateFunction, { client }] = useUpdateAudiencesMutation({ fetchPolicy: 'no-cache' });

    return useCallback(
        async (audiences: AudienceGraphql[]) => {
            const approved = await confirmDialog({
                title: t('title', { count: audiences.length }),
                dataTestId: 'archive-audience-dialog',
                confirmText: t('confirmText'),
                children: t('children', { count: audiences.length })
            });

            if (!approved) {
                return;
            }

            dispatch(addAudiencesProcessingRowIds(AudiencesTabs.Active, audiences));

            await archive({ mutateFunction, audiences, clientCache: client.cache, systemId, store, t: t_without_prefix });

            dispatch(removeAudiencesProcessingRowIds(AudiencesTabs.Active, audiences));
        },
        [t, dispatch, mutateFunction, client.cache, systemId, store, t_without_prefix]
    );
};

export const useDeleteAudiencesMutation = () => {
    const { t } = useTranslation('', { keyPrefix: `${keyPrefix}.useDeleteAudiencesMutation` });
    const dispatch = useDispatch();
    const systemId = useSelector(store => store.common.currentSystem);
    const [mutateFunction, { client }] = _useDeleteAudienceMutation({ fetchPolicy: 'no-cache' });

    return useCallback(
        async (audiences: AudienceGraphql[]) => {
            const approved = await confirmDelete({ count: audiences.length, permanently: true, itemName: 'audience' });

            if (!approved) {
                return;
            }

            dispatch(addAudiencesProcessingRowIds(AudiencesTabs.Archived, audiences));

            try {
                const { data } = await mutateFunction({ variables: { ids: _map(audiences, 'id'), systemId } });
                const deletedIds = data!.deleteAudiences.deletedIds;

                await dispatch(
                    triggerRowAnimationAction({
                        ids: deletedIds,
                        animateType: AppTableRowAnimateType.deleted
                    })
                );

                client.cache.updateQuery({ query: GetAudiencesListsDocument, variables: { systemId } }, ({ getAudiencesLists }) => ({
                    getAudiencesLists: {
                        ...getAudiencesLists,
                        // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                        archivedAudiencesList: getAudiencesLists.archivedAudiencesList.filter(({ id }) => !deletedIds.includes(id))
                    }
                }));

                dispatch(
                    enqueueSnackbar({
                        message: t('audienceWasDeleted', { count: audiences?.length, title: audiences[0].title }),
                        options: { variant: 'success' }
                    })
                );
            } catch (error: $TSFixMe) {
                dispatch(
                    enqueueSnackbar({
                        message: t('couldNotDelete', { count: audiences?.length }),
                        options: { variant: 'error' }
                    })
                );
            } finally {
                dispatch(removeAudiencesProcessingRowIds(AudiencesTabs.Archived, audiences));
            }
        },
        [dispatch, mutateFunction, systemId, client.cache, t]
    );
};

export const useUpdateAudienceMutation = (onError: (error: unknown) => { showSnackBar: boolean; isBusinessLogicError: boolean }) => {
    const { t } = useTranslation('', { keyPrefix: `${keyPrefix}.useUpdateAudienceMutation` });
    const store = useStore<IStore>();
    const dispatch = useDispatch();
    const systemId = useSelector(store => store.common.currentSystem!);
    const [mutateFunction, { client }] = useUpdateAudiencesMutation({ fetchPolicy: 'no-cache' });

    return useCallback(
        async (audience: AudienceUpdateInputType) => {
            const currentTab = store.getState().audiences.currentTab;

            const previousRules = client.readFragment<AudienceGraphql>({
                id: `AudienceGraphql:${audience.id}`,
                fragment: AudienceDataFragmentDoc
            })?.data?.rules;

            const shouldUpdateRules = previousRules ? hasRulesChanged(previousRules, audience.data?.rules) : true;
            const audiences = [parseAudience(audience, OpNames.update)];

            try {
                const { data } = await mutateFunction({
                    variables: {
                        systemId,
                        updateRules: shouldUpdateRules,
                        audiences
                    }
                });

                await client.cache.updateQuery<GetAudiencesListsQuery, GetAudiencesListsQueryVariables>(
                    { query: GetAudiencesListsDocument, variables: { systemId: systemId! } },
                    data2 => {
                        const { getAudiencesLists } = data2!;
                        const audiencesList = getAudiencesLists.audiencesList.filter(item => !data?.updateAudiences.deletedIds.includes(item.id!));

                        data?.updateAudiences.audiences.forEach(audience => {
                            const index = audiencesList.findIndex(item => item.id === audience.id);

                            if (index > -1) {
                                audiencesList[index] = audience;
                            } else {
                                audiencesList.push(audience);
                            }
                        });

                        return { getAudiencesLists: { ...getAudiencesLists, audiencesList } };
                    }
                );

                if (shouldUpdateRules) {
                    client.cache.evict({ id: 'ROOT_QUERY', fieldName: getOperationName(GetRecipientsDocument) as string });
                }

                dispatch(
                    enqueueSnackbar({
                        message: t('audienceUpdated', { title: data!.updateAudiences.audiences[0].title }),
                        options: { variant: 'success' }
                    })
                );

                setTimeout(() => {
                    if (currentTab === AudiencesTabs.Active) {
                        dispatch(
                            triggerRowAnimationAction({
                                ids: _map(data!.updateAudiences.audiences, 'id'),
                                animateType: AppTableRowAnimateType.created
                            })
                        );
                    }
                }, 200);

                return data!.updateAudiences.audiences[0];
            } catch (error: $TSFixMe) {
                const { showSnackBar, isBusinessLogicError } = onError(error);
                if (!isBusinessLogicError) {
                    logger.error('Could not update audience', error);
                }
                if (showSnackBar) {
                    dispatch(
                        enqueueSnackbar({
                            message: t('couldNotUpdate'),
                            options: { variant: 'error' }
                        })
                    );
                }
                throw error;
            }
        },
        [store, client, mutateFunction, systemId, dispatch, t, onError]
    );
};

export const useCreateAudienceMutation = (onError: (error: unknown) => { showSnackBar: boolean; isBusinessLogicError: boolean }) => {
    const { t } = useTranslation('', { keyPrefix: `${keyPrefix}.useCreateAudienceMutation` });
    const store = useStore<IStore>();
    const systemId = useSelector(store => store.common.currentSystem);
    const [mutateFunction, { client }] = _useCreateAudienceMutation({ fetchPolicy: 'no-cache' });

    return useCallback(
        async (audience: AudienceCreateInputType) => {
            try {
                const currentTab = store.getState().audiences.currentTab;

                const { data } = await mutateFunction({
                    variables: {
                        systemId,
                        audience: parseAudience(audience, OpNames.create)
                    }
                });

                client.cache.updateQuery({ query: GetAudiencesListsDocument, variables: { systemId } }, ({ getAudiencesLists }) => ({
                    getAudiencesLists: {
                        ...getAudiencesLists,
                        audiencesList: [...getAudiencesLists.audiencesList, ...data!.createAudience.audiences.map(aud => ({ ...aud }))]
                    }
                }));

                store.dispatch(
                    enqueueSnackbar({
                        message: t('audienceCreated', { title: data!.createAudience.audiences[0].title }),
                        options: { variant: 'success' }
                    })
                );

                if (currentTab === AudiencesTabs.Active) {
                    setTimeout(() => {
                        store.dispatch(
                            triggerRowAnimationAction({
                                ids: _map(data!.createAudience.audiences, 'id'),
                                animateType: AppTableRowAnimateType.created
                            }) as $TSFixMe
                        );
                    }, 200);
                }
                return data!.createAudience.audiences[0];
            } catch (error) {
                const { showSnackBar, isBusinessLogicError } = onError(error);
                if (!isBusinessLogicError) {
                    logger.error('Could not create audience', error);
                }
                if (showSnackBar) {
                    store.dispatch(
                        enqueueSnackbar({
                            message: t('couldNotCreate'),
                            options: { variant: 'error' }
                        })
                    );
                }
                throw error;
            }
        },
        [store, mutateFunction, systemId, client.cache, t, onError]
    );
};

export const getErrorMessageHandlerProps = ({ error, t }: { error: ApolloError; t: TFunction }) => {
    for (const gqlError of error.graphQLErrors) {
        const { extensions } = gqlError;
        // when running tests exception field is not there
        const { response } = extensions?.exception ?? (extensions || {});
        if (response) {
            const { errorType, extraInfo } = response;
            if (errorType === ErrorTypes.AudienceWithSameNameExists) {
                return {
                    showDialog: false,
                    msg: t('nameAlreadyExists'),
                    showSnackBar: false,
                    isBusinessLogicError: true
                };
            }
            if (errorType === ErrorTypes.AudienceWithSameRulesExists) {
                return { showDialog: true, msg: extraInfo, showSnackBar: false, isBusinessLogicError: true };
            }
            if (errorType === AUDIENCE_UPDATE_ERRORS.AudienceIsUsedInNotifications) {
                return { showDialog: false, msg: error.message, showSnackBar: true, isBusinessLogicError: true };
            }
        }
    }
    return { showDialog: false, msg: error.message, showSnackBar: true, isBusinessLogicError: false };
};
